import { useEffect, useState } from "react";
import api from "@/lib/api";
import DataTable from "@/components/admin/DataTable";
import SearchBox from "@/components/admin/SearchBox";
import EmptyState from "@/components/admin/EmptyState";
import StatusBadge from "@/components/admin/StatusBadge";
import ExportCSVButton from "@/components/admin/ExportCSVButton";
import { CreditCard } from "lucide-react";
import AdminPaymentDetail from "./AdminPaymentDetail";

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-AU");
}

function money(n) { return `$${(n ?? 0).toFixed(2)}`; }

function tone(status) {
  if (status === "paid") return "green";
  if (status === "unpaid") return "amber";
  if (status === "owner_bypass") return "grey";
  if (status === "refunded") return "red";
  return "grey";
}

const STATUSES = [
  { v: "all", l: "All" },
  { v: "paid", l: "Paid" },
  { v: "unpaid", l: "Unpaid" },
  { v: "owner_bypass", l: "Owner bypass" },
  { v: "refunded", l: "Refunded" },
];

const COLS = [
  { key: "created_at", label: "Date", render: (r) => relTime(r.created_at) },
  { key: "customer", label: "Customer", render: (r) => (
    <div className="min-w-0">
      <div className="truncate text-sm text-brand-ink">{r.owner_name || "—"}</div>
      <div className="text-xs text-brand-muted font-mono truncate">{r.owner_email || "—"}</div>
    </div>
  )},
  { key: "project_title", label: "Project", render: (r) => (
    <span className="text-sm text-brand-ink truncate">{r.project_title || "—"}</span>
  )},
  { key: "amount_aud", label: "Amount", className: "text-right", render: (r) => money(r.amount_aud) },
  { key: "status", label: "Status", render: (r) => (
    <StatusBadge tone={tone(r.status)} label={r.status} />
  )},
  { key: "session_id", label: "Stripe ref", render: (r) => (
    <span className="font-mono text-xs text-brand-muted">{(r.session_id || r.id || "").slice(0, 18)}…</span>
  )},
];

export default function AdminPayments() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (selectedId) return;
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page), page_size: String(pageSize), status,
    });
    if (q) params.set("q", q);
    api.get(`/admin/payments?${params.toString()}`)
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [page, pageSize, q, status, selectedId]);

  if (selectedId) {
    return (
      <AdminPaymentDetail
        txId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div data-testid="admin-payments">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif text-3xl text-brand-ink tracking-tight">Payments</h2>
          <div className="text-xs text-brand-muted mt-1">
            {data.total} {status === "all" ? "total" : status} transaction{data.total === 1 ? "" : "s"}
          </div>
        </div>
        <ExportCSVButton
          path="/admin/payments/export.csv"
          params={{ q, status: status !== "all" ? status : undefined }}
          filename="colourmatch-payments.csv"
          testId="payments-export-csv"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBox
          value={q}
          onChange={(v) => { setQ(v); setPage(1); }}
          placeholder="Search customer email or Stripe session id…"
          testId="payments-search"
        />
        <div className="inline-flex gap-px bg-brand-linen border border-brand-linen" data-testid="payments-status-pills">
          {STATUSES.map((s) => (
            <button
              key={s.v}
              data-testid={`payments-status-${s.v}`}
              onClick={() => { setStatus(s.v); setPage(1); }}
              className={`px-3 py-1.5 text-xs ${
                status === s.v ? "bg-brand-navy text-white" : "bg-white hover:bg-brand-linen/30"
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>
      </div>

      {err && (
        <div className="text-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3 mb-4">
          {err}
        </div>
      )}

      <DataTable
        testId="payments-table"
        columns={COLS}
        rows={data.items}
        total={data.total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        onRowClick={(row) => setSelectedId(row.id)}
        loading={loading}
        empty={(
          <EmptyState
            icon={CreditCard}
            title={`No ${status === "all" ? "" : status} payments`}
            caption="Try a different status filter or clear the search."
            testId="payments-empty"
          />
        )}
      />
    </div>
  );
}
